import React from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

import {
  FaEdit,
  FaFileAlt,
  FaFolderOpen,
  FaLink,
  FaListOl,
  FaPlus,
  FaSave,
  FaTimes,
  FaTrash,
} from "react-icons/fa";

import {
  DOSSIER_QUILL_FORMATS,
  DOSSIER_QUILL_MODULES,
  safeArray,
} from "../utils/dossierInscriptionUtils";

function Field({ label, children }) {
  return (
    <div>
      <label className="mb-1 block text-sm font-bold text-slate-700 dark:text-slate-200">
        {label}
      </label>

      {children}
    </div>
  );
}

export default function SegmentEditorPanel({
  segment,
  savingSegmentId,
  deletingSegmentId,
  fileForm,
  fileToEditId,
  savingFileId,
  deletingFileId,
  onSegmentChange,
  onSaveSegment,
  onDeleteSegment,
  onFileChange,
  onEditFile,
  onResetFileForm,
  onSaveFile,
  onDeleteFile,
}) {
  const files = safeArray(segment?.fichiers);
  const isSavingSegment = savingSegmentId === segment.id;
  const isDeletingSegment = deletingSegmentId === segment.id;
  const isEditingFile = !!fileToEditId;

  const form = fileForm || {
    nom_fichier: "",
    lien_fichier: "",
    detail_fichier: "",
  };

  return (
    <div className="space-y-5">
      <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
        <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

        <div className="p-4 sm:p-5 lg:p-6">
          <div className="mb-5 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
                Segment sélectionné
              </p>

              <h3 className="mt-1 flex items-center gap-2 text-xl font-bold text-slate-900 dark:text-white">
                <FaFolderOpen className="text-indigo-600 dark:text-indigo-300" />
                {segment.segment_name || "Segment sans nom"}
              </h3>

              <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
                Modifiez le titre, le contenu et l’ordre d’affichage de ce
                segment.
              </p>
            </div>

            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => onSaveSegment(segment.id)}
                disabled={isSavingSegment || isDeletingSegment}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-emerald-600 px-4 py-3 text-sm font-bold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <FaSave />
                {isSavingSegment ? "Enregistrement..." : "Enregistrer"}
              </button>

              <button
                type="button"
                onClick={() => onDeleteSegment(segment.id)}
                disabled={isSavingSegment || isDeletingSegment}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-rose-600 px-4 py-3 text-sm font-bold text-white transition hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <FaTrash />
                {isDeletingSegment ? "Suppression..." : "Supprimer"}
              </button>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-[1fr,180px]">
            <Field label="Nom du segment">
              <input
                type="text"
                value={segment.segment_name || ""}
                onChange={(event) =>
                  onSegmentChange(segment.id, "segment_name", event.target.value)
                }
                placeholder="Ex : Documents à fournir"
                className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-medium text-slate-700 outline-none transition placeholder:text-slate-400 focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
              />
            </Field>

            <Field label="Ordre">
              <div className="relative">
                <FaListOl className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />

                <input
                  type="number"
                  value={segment.ordre_segment ?? 0}
                  onChange={(event) =>
                    onSegmentChange(
                      segment.id,
                      "ordre_segment",
                      Number(event.target.value) || 0
                    )
                  }
                  className="w-full rounded-2xl border border-slate-200 bg-white py-2.5 pl-10 pr-3 text-sm font-medium text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
                />
              </div>
            </Field>
          </div>

          <div className="mt-4">
            <Field label="Contenu du segment">
              <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-950 quill-wrapper">
                <ReactQuill
                  theme="snow"
                  value={segment.segment_content || ""}
                  onChange={(value) =>
                    onSegmentChange(segment.id, "segment_content", value)
                  }
                  placeholder="Contenu du segment"
                  modules={DOSSIER_QUILL_MODULES}
                  formats={DOSSIER_QUILL_FORMATS}
                />
              </div>
            </Field>
          </div>
        </div>
      </section>

      <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
        <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

        <div className="p-4 sm:p-5 lg:p-6">
          <div className="mb-5">
            <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
              Fichiers
            </p>

            <h3 className="mt-1 flex items-center gap-2 text-xl font-bold text-slate-900 dark:text-white">
              <FaFileAlt className="text-indigo-600 dark:text-indigo-300" />
              Documents liés
            </h3>

            <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
              Ajoutez les documents téléchargeables associés à ce segment :
              PDF, autorisations, attestations ou liens externes.
            </p>
          </div>

          <div className="space-y-2">
            {files.map((file) => (
              <article
                key={file.id}
                className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 dark:border-slate-800 dark:bg-slate-950/70 sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="flex min-w-0 items-start gap-3">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-white text-indigo-700 ring-1 ring-slate-200 dark:bg-slate-900 dark:text-indigo-300 dark:ring-slate-700">
                    <FaFileAlt />
                  </span>

                  <div className="min-w-0">
                    <a
                      href={file.lien_fichier}
                      target="_blank"
                      rel="noreferrer"
                      className="line-clamp-1 text-sm font-bold text-indigo-700 hover:underline dark:text-indigo-300"
                      title={file.lien_fichier}
                    >
                      {file.nom_fichier}
                    </a>

                    {file.detail_fichier ? (
                      <p className="mt-1 text-xs leading-5 text-slate-500 dark:text-slate-400">
                        {file.detail_fichier}
                      </p>
                    ) : null}

                    <p className="mt-1 line-clamp-1 text-[11px] text-slate-400">
                      {file.lien_fichier}
                    </p>
                  </div>
                </div>

                <div className="flex shrink-0 flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => onEditFile(file, segment.id)}
                    className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                  >
                    <FaEdit />
                    Modifier
                  </button>

                  <button
                    type="button"
                    onClick={() => onDeleteFile(file.id)}
                    disabled={deletingFileId === file.id}
                    className="inline-flex items-center gap-2 rounded-xl bg-rose-600 px-3 py-2 text-xs font-bold text-white transition hover:bg-rose-700 disabled:opacity-60"
                  >
                    <FaTrash />
                    Supprimer
                  </button>
                </div>
              </article>
            ))}

            {!files.length ? (
              <p className="rounded-2xl bg-slate-50 p-4 text-sm text-slate-500 ring-1 ring-slate-200 dark:bg-slate-950 dark:text-slate-400 dark:ring-slate-800">
                Aucun fichier pour ce segment.
              </p>
            ) : null}
          </div>

          <div className="mt-5 rounded-[24px] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950/70">
            <div className="mb-4 flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-bold text-slate-900 dark:text-white">
                  {isEditingFile ? "Modifier un fichier" : "Ajouter un fichier"}
                </p>

                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                  Nom et lien sont obligatoires. Le détail reste optionnel.
                </p>
              </div>

              {isEditingFile ? (
                <button
                  type="button"
                  onClick={() => onResetFileForm(segment.id)}
                  className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                >
                  <FaTimes />
                  Annuler
                </button>
              ) : null}
            </div>

            <div className="grid gap-3 xl:grid-cols-3">
              <Field label="Nom du fichier">
                <input
                  type="text"
                  value={form.nom_fichier || ""}
                  onChange={(event) =>
                    onFileChange(segment.id, "nom_fichier", event.target.value)
                  }
                  placeholder="Ex : Autorisation parentale"
                  className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-medium text-slate-700 outline-none transition placeholder:text-slate-400 focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
                />
              </Field>

              <Field label="Lien du fichier">
                <div className="relative">
                  <FaLink className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />

                  <input
                    type="text"
                    value={form.lien_fichier || ""}
                    onChange={(event) =>
                      onFileChange(segment.id, "lien_fichier", event.target.value)
                    }
                    placeholder="https://..."
                    className="w-full rounded-2xl border border-slate-200 bg-white py-2.5 pl-10 pr-3 text-sm font-medium text-slate-700 outline-none transition placeholder:text-slate-400 focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
                  />
                </div>
              </Field>

              <Field label="Détail">
                <input
                  type="text"
                  value={form.detail_fichier || ""}
                  onChange={(event) =>
                    onFileChange(segment.id, "detail_fichier", event.target.value)
                  }
                  placeholder="PDF, à imprimer et signer"
                  className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-medium text-slate-700 outline-none transition placeholder:text-slate-400 focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
                />
              </Field>
            </div>

            <div className="mt-4 flex justify-end">
              <button
                type="button"
                onClick={() => onSaveFile(segment.id)}
                disabled={savingFileId === (form.id || `new-${segment.id}`)}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-[#013E8E] px-4 py-3 text-sm font-bold text-white transition hover:bg-[#012f6d] disabled:cursor-not-allowed disabled:opacity-60 dark:bg-indigo-500 dark:hover:bg-indigo-400"
              >
                {isEditingFile ? <FaSave /> : <FaPlus />}
                {isEditingFile ? "Modifier ce fichier" : "Ajouter ce fichier"}
              </button>
            </div>
          </div>
        </div>
      </section>

      <style>{`
        .quill-wrapper .ql-toolbar.ql-snow {
          border: 0;
          border-bottom: 1px solid #e2e8f0;
          background: #ffffff;
        }

        .quill-wrapper .ql-container.ql-snow {
          border: 0;
          min-height: 240px;
        }

        .quill-wrapper .ql-editor {
          min-height: 240px;
          font-size: 14px;
          line-height: 1.7;
        }

        .dark .quill-wrapper .ql-toolbar.ql-snow {
          background: rgba(15,23,42,.98);
          border-color: #334155;
        }

        .dark .quill-wrapper .ql-container.ql-snow {
          background: rgba(2,6,23,.7);
        }

        .dark .quill-wrapper .ql-editor {
          color: #e2e8f0;
        }

        .dark .ql-snow .ql-stroke {
          stroke: #e2e8f0 !important;
        }

        .dark .ql-snow .ql-fill {
          fill: #e2e8f0 !important;
        }

        .dark .ql-snow .ql-picker,
        .dark .ql-snow .ql-picker-label,
        .dark .ql-snow .ql-picker-options,
        .dark .ql-snow .ql-toolbar button {
          color: #e2e8f0 !important;
        }

        .dark .ql-snow .ql-picker-options {
          background: #0b1220;
          border-color: #334155;
        }
      `}</style>
    </div>
  );
}